import { useState } from 'react';
import { useAuth } from '@auth-wrapper';
import { notifyPromiseWithProgress } from '@components/atoms/toast/services';
import { GENERAL_SIMULATION_PATH, GENERAL_SIMPLIFIED_PROPOSAL_PATH } from '@routers/constants';
import {
  COMPLETE_PROPOSAL_DOCUMENT_POLL_MAX_ATTEMPTS,
  requireDownloadUrl,
  requireDownloadUrls,
  useProposalDocumentGenerator,
} from '@hooks/proposal-documents';
import { buildPublicProposalUrl } from '@utils/public-proposal-url';

export interface SimulationDocumentExportState {
  loading: boolean;
  pdfLink?: string;
  imageLinks?: string[];
  errorMessage?: string;
  exportDocument: (params: { leadName: string; simulationId: string; format: 'png' }) => void;
  exportCompleteProposalPdf: (params: {
    leadName: string;
    simulationId: string;
    nUnits: number;
    hasAccounts?: boolean;
    unitIds?: string[];
  }) => Promise<void>;
  exportSimplifiedProposalPdf: (params: { leadName: string; simulationId: string; nUnits: number }) => void;
}
export const PROPOSAL_DOCUMENT_DIMENSIONS = { w: 1920, h: 1080 };
export const A4_DOCUMENT_DIMENSIONS = { w: 794, h: 1123 };

const printUrl = (path: string, simulationId: string) =>
  buildPublicProposalUrl(`${path.replace(':simulationId', simulationId)}?print`);

const errorMessageOf = (error: unknown): string => {
  const candidate = error as { message?: string };
  return candidate?.message ?? String(error);
};

export function useSimulationDocumentExport(): SimulationDocumentExportState {
  const { token } = useAuth();
  const { generateProposalDocument } = useProposalDocumentGenerator();
  const [loading, setLoading] = useState(false);
  const [pdfLink, setPdfLink] = useState<string | undefined>(undefined);
  const [imageLinks, setImageLinks] = useState<string[] | undefined>(undefined);
  const [errorMessage, setErrorMessage] = useState<string | undefined>(undefined);

  const beginExport = (): boolean => {
    if (!token) {
      setErrorMessage('Token de autenticação não encontrado');
      return false;
    }
    setLoading(true);
    setPdfLink(undefined);
    setImageLinks(undefined);
    setErrorMessage(undefined);
    return true;
  };

  const failExport = (error: unknown) => {
    setPdfLink(undefined);
    setImageLinks(undefined);
    setErrorMessage(errorMessageOf(error));
  };

  const exportDocument = ({ simulationId }: { leadName: string; simulationId: string; format: 'png' }) => {
    if (!beginExport()) return;

    const promise = generateProposalDocument({
      simulationId,
      simulationType: 'ML',
      kind: 'SLIDES_PNG',
      sourceUrl: printUrl(GENERAL_SIMULATION_PATH, simulationId),
      width: PROPOSAL_DOCUMENT_DIMENSIONS.w,
      height: PROPOSAL_DOCUMENT_DIMENSIONS.h,
    });

    notifyPromiseWithProgress({
      promise,
      loadingMessage: 'Gerando documento. Aguarde...',
      successMessage: 'Documento gerado com sucesso!',
      errorMessage: 'Houve um erro ao gerar documento. Tente novamente.',
      setIsLoading: setLoading,
    })
      .then((document) => {
        setImageLinks(requireDownloadUrls(document));
        setPdfLink(undefined);
        setErrorMessage(undefined);
      })
      .catch(failExport);
  };

  const exportCompleteProposalPdf = async ({
    simulationId,
    nUnits,
    hasAccounts = false,
    unitIds = [],
  }: {
    leadName: string;
    simulationId: string;
    nUnits: number;
    hasAccounts?: boolean;
    unitIds?: string[];
  }) => {
    if (!beginExport()) return;

    const promise = generateProposalDocument(
      {
        simulationId,
        simulationType: 'ML',
        kind: 'COMPLETE_PDF',
        sourceUrl: printUrl(GENERAL_SIMULATION_PATH, simulationId),
        width: PROPOSAL_DOCUMENT_DIMENSIONS.w,
        height: PROPOSAL_DOCUMENT_DIMENSIONS.h,
        nUnits,
        hasAccounts,
        unitIds,
      },
      { maxAttempts: COMPLETE_PROPOSAL_DOCUMENT_POLL_MAX_ATTEMPTS },
    );

    await notifyPromiseWithProgress({
      promise,
      loadingMessage: 'Gerando Proposta Completa. Aguarde...',
      successMessage: 'Proposta Completa gerada com sucesso!',
      errorMessage: 'Houve um erro ao gerar a Proposta Completa. Tente novamente.',
      setIsLoading: setLoading,
    })
      .then((document) => {
        setPdfLink(requireDownloadUrl(document));
        setErrorMessage(undefined);
      })
      .catch(failExport);
  };

  const exportSimplifiedProposalPdf = ({
    simulationId,
    nUnits,
  }: {
    leadName: string;
    simulationId: string;
    nUnits: number;
  }) => {
    if (!beginExport()) return;

    const promise = generateProposalDocument({
      simulationId,
      simulationType: 'ML',
      kind: 'SIMPLIFIED_PDF',
      sourceUrl: printUrl(GENERAL_SIMPLIFIED_PROPOSAL_PATH, simulationId),
      width: PROPOSAL_DOCUMENT_DIMENSIONS.w,
      height: PROPOSAL_DOCUMENT_DIMENSIONS.h,
      nUnits,
    });

    notifyPromiseWithProgress({
      promise,
      loadingMessage: 'Gerando PDF Simplificada. Aguarde...',
      successMessage: 'PDF Simplificada gerado com sucesso!',
      errorMessage: 'Houve um erro ao gerar o PDF. Tente novamente.',
      setIsLoading: setLoading,
    })
      .then((document) => {
        setPdfLink(requireDownloadUrl(document));
        setImageLinks(undefined);
        setErrorMessage(undefined);
      })
      .catch(failExport);
  };

  return {
    loading,
    pdfLink,
    imageLinks,
    errorMessage,
    exportDocument,
    exportCompleteProposalPdf,
    exportSimplifiedProposalPdf,
  };
}
